import { useNavigate } from 'react-router-dom'
import { Anchor, Badge, Text } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { DataTable, EmptyState, SectionAction, SectionCard, StatusBadge } from '@lighthouse/design'
import type { DataTableColumn } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { useRegistry } from '../../hooks/useRegistry'
import { isApiError } from '../../services/apiError'
import { errorKind } from '../../utils/format'
import { reportDetailPath } from '../../utils/reportDetailPath'
import type { RegistryReport, RegistryReportStatus } from '../../types/registry'
import {
  REGISTRY_PREVIEW_SIZE, REGISTRY_SURFACE, grantsText, lastRunWhen, ownerCell, registryErrorText,
  registryFetchEnabled, registryPreviewSubtitle, runnableNote, statusPresentation,
} from './registryFormat'

/** The preview's one question: the first five reports by name, no filter. Stable, so it is one cache entry. */
const PREVIEW_QUERY = { skip: 0, top: REGISTRY_PREVIEW_SIZE } as const

const COLUMNS: DataTableColumn<RegistryReport>[] = [
  { key: 'name', header: 'Report', render: (r) => <PreviewReportName report={r} /> },
  { key: 'owner', header: 'Owner', width: 220, render: (r) => <PreviewOwner report={r} /> },
  { key: 'status', header: 'Status', width: 170, render: (r) => <PreviewStatus report={r} /> },
  {
    // People only: group or role grants reach nobody, and the page shows them apart.
    key: 'people', header: 'People granted', width: 120, numeric: true,
    render: (r) => grantsText(r.userGrantCount),
  },
  {
    key: 'lastRun', header: 'Last recorded run', width: 140, numeric: true,
    render: (r) => lastRunWhen(r.lastRunAt).text,
  },
]

/**
 * The Overview's Report Registry card.
 *
 * Fetches ONLY when the server has said the caller holds Lighthouse.Admin
 * (gate `admin`): the Overview is every operator's landing page, and a card
 * there must not call an endpoint the caller may not read. `denied` renders the
 * compact restricted state; `unknown` waits for the answer and says so if the
 * catalogue that carries it could not be read.
 *
 * No toolbar: searching and filtering are the page's (`/registry`), and a
 * search box here would either duplicate it or pretend to.
 *
 * A row opens that report's Report Detail (`/registry/:reportId`, D5), as the
 * page's rows do; the name is the keyboard way in, since a row takes no focus.
 */
export function ReportRegistryPreview() {
  const navigate = useNavigate()
  const gate = useLighthouseAdmin()
  const catalog = useEtlJobCatalog()
  const { data, isLoading, isError, error } = useRegistry(PREVIEW_QUERY, {
    enabled: registryFetchEnabled(gate, 'preview'),
  })

  const loaded = gate.state === 'admin' && !isError && !isLoading && data != null

  return (
    <SectionCard
      title="Report Registry"
      subtitle={loaded ? registryPreviewSubtitle(data) : undefined}
      flush
      // No way in for a caller the server has refused: the page could only show the restricted state.
      action={gate.state === 'denied'
        ? undefined
        : <SectionAction onClick={() => navigate('/registry')}>View all →</SectionAction>}
    >
      {gate.state === 'denied' ? (
        <div style={{ padding: 12 }}>
          <RestrictedState compact surface={REGISTRY_SURFACE} reason={gate.reason} />
        </div>
      ) : gate.state === 'unknown' ? (
        catalog.isError ? (
          <EmptyState
            icon={<IconAlertCircle size={28} stroke={1.5} />}
            title="Could not determine access"
            message="The server's answer on Lighthouse.Admin did not load, so this card has not asked the registry. The full page asks it directly."
          />
        ) : catalog.data ? (
          // Loaded, and still no answer: an API that predates the permission field. Waiting would be forever.
          <EmptyState
            icon={<IconAlertCircle size={28} stroke={1.5} />}
            title="Access not stated"
            message="The server did not say whether you hold Lighthouse.Admin, so this card has not asked the registry. The full page asks it directly."
          />
        ) : (
          <DataTable columns={COLUMNS} rows={[]} rowKey={(r) => r.reportId} loading skeletonRows={REGISTRY_PREVIEW_SIZE} />
        )
      ) : isError ? (
        errorKind(error) === 'forbidden' ? (
          <div style={{ padding: 12 }}>
            <RestrictedState compact surface={REGISTRY_SURFACE} reason={isApiError(error) ? error.detail : null} />
          </div>
        ) : (
          <EmptyState
            icon={<IconAlertCircle size={28} stroke={1.5} />}
            title="Could not load the report registry"
            message={`${registryErrorText(error)} This says nothing about which reports exist.`}
          />
        )
      ) : isLoading || !data ? (
        <DataTable columns={COLUMNS} rows={[]} rowKey={(r) => r.reportId} loading skeletonRows={REGISTRY_PREVIEW_SIZE} />
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={data.reports}
          rowKey={(r) => r.reportId}
          onRowClick={(r) => navigate(reportDetailPath(r.reportId))}
          empty={
            <EmptyState
              title="No reports in the registry"
              message="Cartographer holds no saved report yet. Reports appear here, in every status, once someone saves one."
            />
          }
        />
      )}
    </SectionCard>
  )
}

/** The report's name, and the keyboard way into its Report Detail. stopPropagation: the row navigates too. */
function PreviewReportName({ report }: { report: RegistryReport }) {
  const navigate = useNavigate()
  const to = reportDetailPath(report.reportId)
  return (
    <Anchor
      component="button"
      type="button"
      size="sm"
      ta="left"
      data-report-detail={to}
      aria-label={`Open ${report.name}`}
      onClick={(event) => {
        event.stopPropagation()
        navigate(to)
      }}
    >
      {report.name}
    </Anchor>
  )
}

/** A lifecycle status: a status badge while the report can still be in play, a plain gray badge after. */
export function RegistryStatus({ status }: { status: RegistryReportStatus }) {
  const shown = statusPresentation(status)
  return shown.kind === 'status'
    ? <StatusBadge status={shown.role} label={shown.label} />
    : <Badge variant="light" color="gray">{shown.label}</Badge>
}

/** The status, with the server's not-runnable note when the row's counts are view-only. */
function PreviewStatus({ report }: { report: RegistryReport }) {
  const note = runnableNote(report)
  return (
    <div>
      <RegistryStatus status={report.status} />
      {note && <Text size="xs" c="dimmed">{note}</Text>}
    </div>
  )
}

/** The owner as the page renders it, shortened to fit: the group note stays, because it is the finding. */
function PreviewOwner({ report }: { report: RegistryReport }) {
  const owner = ownerCell(report)
  return (
    <div title={owner.tooltip ?? undefined}>
      <Text size="sm" style={{ overflowWrap: 'anywhere' }}>{owner.text}</Text>
      {owner.note && <Text size="xs" c="dimmed" lineClamp={2}>{owner.note}</Text>}
    </div>
  )
}
